import { Exclude, Expose, plainToClass, Type } from "class-transformer";
import * as t from "io-ts";
import "reflect-metadata";
import { v4 as uuid } from "uuid";
import { CompanyRegionProfileArrayValidator, CompanyRegionProfileEntity } from "./company-region-profile";
import { Entity } from "./entity";
import { DatabaseCode, toDatabaseCode } from "./database-code";

/** Validator for a company. */
export const CompanyValidator = t.intersection([
  t.type({
    id: t.string,
    name: t.string,
    type: t.string,
    doNotUse: t.boolean,
  }),
  t.partial({
    primaryCompany: t.boolean,
    companyRegionProfileList: CompanyRegionProfileArrayValidator,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

/** Validator for an array of companies. */
export const CompanyArrayValidator = t.array(CompanyValidator);

export type Company = t.TypeOf<typeof CompanyValidator>;
export type CompanyArray = t.TypeOf<typeof CompanyArrayValidator>;

export type CompanyType = DatabaseCode;

/* istanbul ignore next */
const operator: CompanyType = {
  code: 'Operator',
  displayValue: {
    id: 'ots.ui.entity.company.type.operator',
    defaultMessage: 'Operator',
  },
};

/* istanbul ignore next */
const truckCompany: CompanyType = {
  code: 'Truck Company',
  displayValue: {
    id: 'ots.ui.entity.company.type.truck',
    defaultMessage: 'Truck Company',
  },
};

/* istanbul ignore next */
const vendor: CompanyType = {
  code: 'Vendor',
  displayValue: {
    id: 'ots.ui.entity.company.type.vendor',
    defaultMessage: 'Vendor',
  },
};
export const companyType = {
  operator,
  truckCompany,
  vendor,
};

export const companyTypes = Object.values(companyType)

export const toCompanyType = (code: string): CompanyType => toDatabaseCode(code, companyTypes)

/**
 * A company.
 */
@Exclude()
export class CompanyEntity implements Entity {
  @Expose() id!: string;
  @Expose() name!: string;
  @Expose({ name: 'type' }) private _type!: string;
  @Expose({ name: 'companyRegionProfileList' })
  @Type(() => CompanyRegionProfileEntity)
  regionProfiles: CompanyRegionProfileEntity[] = [];
  @Expose() doNotUse = false;
  @Expose() primaryCompany?: boolean;
  @Expose() createDttm?: number;
  @Expose() lastModifiedDttm?: number;

  readonly entityType = 'COMPANY';

  get displayName(): Readonly<string> {
    return this.name;
  }

  /**
   * Constructor for CompanyEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  /**
   * Create a CompanyEntity.
   *
   * @param name The name.
   * @param type The type of company.
   * @param id (optional) The primary key of the company.
   */
  static create(
    name: string,
    type: CompanyType | Readonly<CompanyType>,
    id: string | undefined = uuid()
  ): CompanyEntity {
    const entity = new CompanyEntity();
    entity.id = id;
    entity.name = name;
    entity._type = type.code;
    return entity;
  }

  /**
   * Create a CompanyEntity of type operator.
   *
   * @param name The name.
   * @param id (optional) The primary key of the company.
   */
  static createOperator(
    name: string,
    id: string | undefined = uuid()
  ): CompanyEntity {
    return this.create(name, operator, id);
  }

  /**
   * Create a CompanyEntity of type truck company.
   *
   * @param name The name.
   * @param id (optional) The primary key of the company.
   */
  static createTruckingCompany(
    name: string,
    id: string | undefined = uuid()
  ): CompanyEntity {
    return this.create(name, truckCompany, id);
  }

  /**
   * Create a CompanyEntity from a Company type value.
   *
   * @param company The company type value.
   */
  static from(company: Company): CompanyEntity {
    const entity: CompanyEntity = plainToClass(CompanyEntity, company, {
      excludeExtraneousValues: true,
    });
    return entity;
  }

  get type(): CompanyType {
    /* istanbul ignore next */
    return toCompanyType(this._type);
  }

  set type(type: CompanyType) {
    /* istanbul ignore next */
    this._type = type.code;
  }

  /**
   * Returns the company's region profile for the region.
   *
   * @param regionId The primary key of the region.
   */
  getRegionProfile(regionId: string): CompanyRegionProfileEntity | undefined {
    return this.regionProfiles.find((p) => p.regionId === regionId);
  }

  /**
   * Returns true if the company is active in the region.
   *
   * If a region profile could not be found, defaults to false.
   *
   * @param regionId The primary key of the region.
   */
  isActive(regionId: string): boolean {
    const regionProfile = this.getRegionProfile(regionId);
    return regionProfile ? regionProfile.isActive : false;
  }
}

export class RegionCompanyEntity {
  readonly company: CompanyEntity;
  readonly profile: CompanyRegionProfileEntity;

  private constructor(
    company: CompanyEntity,
    profile: CompanyRegionProfileEntity
  ) {
    this.company = company;
    this.profile = profile;
  }

  static create(
    company: CompanyEntity,
    regionId: string
  ): RegionCompanyEntity | undefined {
    const profile = company.getRegionProfile(regionId);
    return profile ? new RegionCompanyEntity(company, profile) : undefined;
  }

  get id(): string {
    return this.company.id;
  }

  get type(): CompanyType {
    return this.company.type;
  }

  get doNotUse(): boolean {
    return this.company.doNotUse;
  }

  get isActive(): boolean {
    return this.profile.isActive;
  }

  get isDispatchRequestEnabled(): boolean {
    return this.profile.isDispatchRequestEnabled;
  }
}
